import { Interval } from "@ocktra/shared-types";

type ToAndFromDates = {
    from: number;
    to: number;
};

type MarketConfig = {
    timezone: string;
    marketOpenHour: number;
    marketOpenMinute: number;
    isWeekendClosed: boolean;
    closedDays: number[]; // 0 = Sunday, 6 = Saturday
};

type CountryCode = 'US' | 'UK' | 'CA' | 'AU' | 'IN' | 'SG' | 'DE' | 'JP';

class Dates {
    private offset: number = 86400; // seconds in a day

    // Market configurations by country
    private marketConfigs: Record<CountryCode, MarketConfig> = {
        US: {
            timezone: 'America/New_York',
            marketOpenHour: 9,
            marketOpenMinute: 30,
            isWeekendClosed: true,
            closedDays: [0, 6], // Sunday, Saturday
        },
        UK: {
            timezone: 'Europe/London',
            marketOpenHour: 8,
            marketOpenMinute: 0,
            isWeekendClosed: true,
            closedDays: [0, 6],
        },
        CA: {
            timezone: 'America/Toronto',
            marketOpenHour: 9,
            marketOpenMinute: 30,
            isWeekendClosed: true,
            closedDays: [0, 6],
        },
        AU: {
            timezone: 'Australia/Sydney',
            marketOpenHour: 10,
            marketOpenMinute: 0,
            isWeekendClosed: true,
            closedDays: [0, 6],
        },
        IN: {
            timezone: 'Asia/Kolkata',
            marketOpenHour: 9,
            marketOpenMinute: 15,
            isWeekendClosed: true,
            closedDays: [0, 6],
        },
        SG: {
            timezone: 'Asia/Singapore',
            marketOpenHour: 9,
            marketOpenMinute: 0,
            isWeekendClosed: true,
            closedDays: [0, 6],
        },
        DE: {
            timezone: 'Europe/Berlin',
            marketOpenHour: 9,
            marketOpenMinute: 0,
            isWeekendClosed: true,
            closedDays: [0, 6],
        },
        JP: {
            timezone: 'Asia/Tokyo',
            marketOpenHour: 9,
            marketOpenMinute: 0,
            isWeekendClosed: true,
            closedDays: [0, 6],
        },
    };

    private currentCountry: CountryCode = 'US';

    /**
     * Set the country for market calculations
     */
    setCountry(country: CountryCode): void {
        this.currentCountry = country;
    }

    /**
     * Get the current market configuration
     */
    private getMarketConfig(): MarketConfig {
        return this.marketConfigs[this.currentCountry];
    }

    /**
     * Get number of data points based on timeframe
     */
    private getNumberOfDays = (timeframe: Interval): number => {
        switch (timeframe) {
            case '1min':
                return 5; // 5 trading days
            case '5min':
                return 10;
            case '15min':
                return 24;
            case '30min':
                return 60;
            case '60min':
                return 90 ;
            case '1day':
                return 200; // ~1 year
            case '1week':
                return 100; // ~10 years
            default:
                return 1;
        }
    };

    /**
     * Check if a given day is a market trading day
     */
    private isTradingDay(date: Date, config: MarketConfig): boolean {
        const dayOfWeek = date.getUTCDay();
        return !config.closedDays.includes(dayOfWeek);
    }

    /**
     * Calculate 'to' and 'from' dates for data fetching
     */
    getToAndFromDates = (
        timeframe: Interval,
        last?: number,
        country?: CountryCode
    ): ToAndFromDates => {
        // Update country if provided
        if (country) {
            this.setCountry(country);
        }

        const config = this.getMarketConfig();
        let from: number, to: number;
        let d: Date = new Date();

        // If 'last' timestamp provided, adjust from that point
        if (last !== undefined) {
            last = last - this.offset;
            d = new Date(last * 1000);
        }

        const offset = d.getTimezoneOffset();

        // Calculate 'to' date at market open time
        to =
            new Date(
                d.getUTCFullYear(),
                d.getUTCMonth(),
                d.getUTCDate(),
                10 - Math.floor(offset / 60),
                0 - (offset % 60),
                0
            ).getTime() / 1000;

        let tempTo = to;
        const numberOfDays = this.getNumberOfDays(timeframe);
        let count = numberOfDays;

        // Count backwards through trading days
        while (count > 0) {
            tempTo -= 86400; // Go back one day
            const checkDate = new Date(tempTo * 1000);

            if (this.isTradingDay(checkDate, config)) {
                count--;
            }
        }

        // Adjust to market open time of the 'from' date
        from = tempTo - 22500; // Additional offset (6.25 hours)

        return { from, to };
    };

    /**
     * Get the next trading day from a given date
     */
    getNextTradingDay(date: Date, country?: CountryCode): Date {
        if (country) {
            this.setCountry(country);
        }

        const config = this.getMarketConfig();
        let nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);

        while (!this.isTradingDay(nextDay, config)) {
            nextDay.setDate(nextDay.getDate() + 1);
        }

        return nextDay;
    }

    /**
     * Get the previous trading day from a given date
     */
    getPreviousTradingDay(date: Date, country?: CountryCode): Date {
        if (country) {
            this.setCountry(country);
        }

        const config = this.getMarketConfig();
        let prevDay = new Date(date);
        prevDay.setDate(prevDay.getDate() - 1);

        while (!this.isTradingDay(prevDay, config)) {
            prevDay.setDate(prevDay.getDate() - 1);
        }

        return prevDay;
    }

    /**
     * Check if market is currently open for a given country
     */
    isMarketOpen(country?: CountryCode): boolean {
        if (country) {
            this.setCountry(country);
        }

        const config = this.getMarketConfig();
        const now = new Date();

        // Check if today is a trading day
        if (!this.isTradingDay(now, config)) {
            return false;
        }

        // Get current time in market timezone (simplified)
        const currentHour = now.getUTCHours();
        const currentMinute = now.getUTCMinutes();
        const currentTime = currentHour * 60 + currentMinute;

        // Market hours (simplified - you may want to add market close time too)
        const openTime = config.marketOpenHour * 60 + config.marketOpenMinute;
        const closeTime = openTime + 6.5 * 60; // Typical 6.5 hour trading day

        return currentTime >= openTime && currentTime <= closeTime;
    }
}

export default Dates;